import axios from 'axios';
import { createAction } from 'redux-actions';
import { call, put, takeEvery } from 'redux-saga/effects';
import { block, unblock } from './BlockDisplayModule';
import { LOGIN_PATH, MAIN_PATH, PATH_DELIMITER } from '../constant/Constant';
import { handleShow } from './ErrorPopupModule';
import { handleLogout } from './LoginModule';
import AuthenticationUtil from '../util/AuthenticationUtil';
import NullCheckUtil from '../util/NullCheckUtil';
import { sessionValidTime } from '../util/DateTimeUtil';
import SessionStorageUtil from '../util/SessionStorageUtil';

const BASE = 'api';
const ACTION_REQUEST = BASE + PATH_DELIMITER + 'ACTION_REQUEST';
const ACTION_UPLOAD = BASE + PATH_DELIMITER + 'ACTION_UPLOAD';
const ACTION_DOWNLOAD = BASE + PATH_DELIMITER + 'ACTION_DOWNLOAD';

export const actionRequest = createAction(ACTION_REQUEST);
export const actionUpload = createAction(ACTION_UPLOAD);
export const actionDownload = createAction(ACTION_DOWNLOAD);

export function* apiSagas() {
    yield takeEvery(ACTION_REQUEST, request);
    yield takeEvery(ACTION_UPLOAD, request);
    yield takeEvery(ACTION_DOWNLOAD, request);
}

export let axiosDefaultConfig = {
    // TODO fix timeout to 30000
    timeout: 30000,
    validateStatus: function (status) {
        return status < 500;
    },
};

export let axiosConfig = {
    ...axiosDefaultConfig,
    headers: {
        'Content-type': 'application/json; charset=UTF-8',
        Accept: '*/*',
    },
};

export let axiosUploadConfig = {
    ...axiosDefaultConfig,
    headers: { 'Content-Type': 'multipart/form-data' },
};

export let axiosDownloadConfig = {
    ...axiosDefaultConfig,
    responseType: 'blob',
};

export function* request({
    method,
    url,
    params,
    data,
    upload = false,
    download = false,
    downloadDirect = false,
    downloadType = 'application/pdf',
    callback,
    uploadProgress,
    downloadProgress,
}) {
    try {
        yield put(block());

        // console.log('REQUEST: ', method, url, JSON.stringify(params), JSON.stringify(data));

        const config = upload ? axiosUploadConfig : download ? axiosDownloadConfig : axiosConfig;
        const response = yield call(axios.request, {
            ...config,
            method: method,
            url: url,
            params: params,
            data: data,
            onUploadProgress: function (e) {
                uploadProgress && uploadProgress(e.loaded);
            },
            onDownloadProgress: function (e) {
                downloadProgress && downloadProgress(e.loaded);
            },
        });

        // console.log('RESPONSE: ' + JSON.stringify(response));
        SessionStorageUtil.setSessionValidTime(sessionValidTime());

        switch (response.status) {
            case 200:
                callback && callback(response.data);
                if (downloadDirect) {
                    console.log('Download Direct');
                    const blob = new Blob([response.data], { type: downloadType });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);

                    const contentDisposition = response.headers['content-disposition'];
                    let fileName = 'unknown';
                    if (contentDisposition) {
                        const [fileNameMatch] = contentDisposition.split(';').filter((str) => str.includes('filename'));
                        if (fileNameMatch) [, fileName] = fileNameMatch.split('=');
                    }

                    link.setAttribute('download', decodeURIComponent(fileName));
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    return null;
                } else if (download) {
                    const blob = new Blob([response.data], { type: downloadType });
                    const url = URL.createObjectURL(blob);
                    return url;
                } else {
                    return response.data;
                }
            case 401:
                yield put({
                    type: handleShow,
                    payload: {
                        message: '로그인 정보가 유효하지 않습니다. 다시 로그인하세요.',
                        goPage: LOGIN_PATH,
                    },
                });
                AuthenticationUtil.clear();
                yield put({ type: handleLogout });
                break;
            case 403:
                yield put({ type: handleShow, payload: { message: '처리 권한이 없습니다.' } });
                break;
            case 404:
                yield put({
                    type: handleShow,
                    payload: {
                        message: '유효하지 않은 주소가 호출되었습니다. Home으로 이동합니다. ',
                        goPage: MAIN_PATH,
                    },
                });
                break;
            default:
                yield put({
                    type: handleShow,
                    payload: { message: '알 수 없는 오류가 발생되었습니다. [' + response.status + ']' },
                });
                break;
        }
    } catch (error) {
        if (
            error.request.responseType === 'blob' &&
            error.response.data instanceof Blob &&
            error.response.data.type &&
            error.response.data.type.toLowerCase().indexOf('json') != -1
        ) {
            error.response.data = JSON.parse(yield error.response.data.text());
        }

        if (NullCheckUtil.isNotNull(error.response)) {
            if (NullCheckUtil.isNotNull(error.response.data)) {
                yield put({
                    type: handleShow,
                    payload: {
                        message:
                            error.response.data.message != undefined
                                ? error.response.data.message
                                : '알 수 없는 오류가 발생되었습니다.',
                    },
                });
            } else {
                yield put({
                    type: handleShow,
                    payload: {
                        message: '알 수 없는 오류가 발생되었습니다.',
                    },
                });
            }
        } else {
            yield put({
                type: handleShow,
                payload: {
                    message: '알 수 없는 오류가 발생되었습니다.',
                },
            });
        }

        console.error(
            'METHOD: ' +
                method +
                ', URL: ' +
                url +
                ' API호출 오류. ERROR: ' +
                JSON.stringify(error) +
                'INTERNAL-ERROR: ' +
                error.name +
                '(' +
                error.message +
                ')',
        );
    } finally {
        yield put(unblock());
    }
}
